// Slim advisor stream client (Phase 6).
// Tools now execute SERVER-SIDE in the orchestrator; this file only consumes the
// typed SSE events (delta / tool_start / tool_result / done) and forwards them to
// the Copilot drawer, which renders the typed result cards.
import { API } from "../api";

// Stream a chat turn. Calls the provided callbacks as typed SSE events arrive.
// Returns { tools, finalText, blocked }.
export async function streamChat({ message, history, callbacks }) {
  const cb = callbacks || {};
  const token = localStorage.getItem("sa_token");
  const resp = await fetch(`${API}/ai/chat/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ message, history: history || [] }),
  });

  if (resp.status === 429 || resp.status === 402) {
    let payload = {};
    try { payload = await resp.json(); } catch (e) { console.warn("streamChat: could not parse block payload", e); }
    const detail = payload?.detail || {};
    if (cb.onBlocked) cb.onBlocked(resp.status, detail);
    return { tools: [], finalText: "", blocked: true };
  }
  if (!resp.ok || !resp.body) {
    const detail = await resp.text().catch(() => `HTTP ${resp.status}`);
    throw new Error(detail || `HTTP ${resp.status}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let streamedText = "";
  let finalText = "";
  let tools = [];

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const rawEvent = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const dataLine = rawEvent.split("\n").find((l) => l.startsWith("data:"));
      if (!dataLine) continue;
      const jsonStr = dataLine.slice(5).trim();
      if (!jsonStr) continue;
      let evt;
      try { evt = JSON.parse(jsonStr); } catch (err) { continue; }

      if (evt.type === "delta" && evt.text) {
        streamedText += evt.text;
        if (cb.onDelta) cb.onDelta(streamedText);
      } else if (evt.type === "quota" && evt.quota) {
        if (cb.onQuota) cb.onQuota(evt.quota);
      } else if (evt.type === "tool_start") {
        tools = [...tools, { tool: evt.tool, args: evt.args, status: "running" }];
        if (cb.onTools) cb.onTools(tools);
      } else if (evt.type === "tool_result") {
        // Fill the most recent running entry for this tool (execution is sequential).
        let target = -1;
        for (let i = tools.length - 1; i >= 0; i--) {
          if (tools[i].tool === evt.tool && tools[i].status === "running") { target = i; break; }
        }
        if (target === -1) {
          tools = [...tools, { tool: evt.tool, args: evt.args, status: "done", result: evt.result }];
        } else {
          tools = tools.map((t, i) => (i === target ? { ...t, status: "done", result: evt.result } : t));
        }
        if (cb.onTools) cb.onTools(tools);
      } else if (evt.type === "done" && evt.text) {
        finalText = evt.text;
        if (cb.onDone) cb.onDone(finalText);
      } else if (evt.type === "error") {
        throw new Error(evt.detail || "stream error");
      }
    }
  }
  return { tools, finalText, blocked: false };
}
